// src/pages/AnalyzedReportsPage.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText, ArrowRight, Download, Calendar, Building2,
  Hash, RefreshCw, Search, Trash2, AlertTriangle, LayoutGrid,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getReports, getReportOutputUrl, getReportInputUrl, deleteReport } from '../utils/api';

// ── helpers ───────────────────────────────────────────────────────────────────

function usd(n) {
  if (typeof n !== 'number' || isNaN(n)) return '—';
  return '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function groupByDate(reports) {
  const groups = {};
  for (const r of reports) {
    const d = r.date || r.processedAt?.slice(0, 10) || 'Unknown';
    if (!groups[d]) groups[d] = [];
    groups[d].push(r);
  }
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
}

function formatDate(dateStr) {
  try {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return dateStr; }
}

function formatTime(isoStr) {
  try {
    return new Date(isoStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

// ── Delete confirmation modal ─────────────────────────────────────────────────

function DeleteModal({ report, onConfirm, onCancel, loading }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{
        background: 'var(--navy-2)', border: '1px solid rgba(248,81,73,0.35)',
        borderRadius: 14, padding: '28px 32px', maxWidth: 460, width: '100%',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8, flexShrink: 0,
            background: 'rgba(248,81,73,0.12)', border: '1px solid rgba(248,81,73,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AlertTriangle size={18} color="var(--red)" />
          </div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Delete Report</div>
        </div>

        <p style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.6 }}>
          This will permanently delete <strong style={{ color: 'var(--text)' }}>{report.inputFilename}</strong> and
          its analysis from the server. This action cannot be undone.
        </p>
        <div style={{
          background: 'rgba(248,81,73,0.07)', border: '1px solid rgba(248,81,73,0.2)',
          borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--red)', marginBottom: 22,
        }}>
          Both the input file and the generated .docx will be deleted.
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={loading}
            style={{ background: 'rgba(248,81,73,0.15)', borderColor: 'rgba(248,81,73,0.4)' }}>
            {loading ? <><span className="spinner" style={{ borderTopColor: 'var(--red)' }} /> Deleting…</> : <><Trash2 size={13} /> Delete</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Input file box helper ────────────────────────────────────────────────────

function InputFileBox({ label, filename, hasFile, downloadUrl }) {
  return (
    <div style={{
      flex: 1, minWidth: 180,
      background: 'var(--navy-3)', border: '1px solid var(--border)',
      borderRadius: 9, padding: '10px 14px',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <FileText size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', wordBreak: 'break-all', lineHeight: 1.4 }}>
        {filename}
      </div>
      {hasFile ? (
        <a href={downloadUrl} download={filename} className="btn btn-secondary btn-sm"
          style={{ textDecoration: 'none', alignSelf: 'flex-start', marginTop: 2 }}>
          <Download size={11} /> Download
        </a>
      ) : (
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>File not stored</span>
      )}
    </div>
  );
}

// ── Single report card ────────────────────────────────────────────────────────

function ReportCard({ report, onDelete }) {
  const m = report.metrics;
  const hasInput = !!report.inputPath;
  const isMultiInput = report.extraInputFiles && report.extraInputFiles.length > 0;
  const isExcel = (report.outputExt || '').toLowerCase() === '.xlsx' ||
    (report.outputFilename || '').toLowerCase().endsWith('.xlsx');
  const outputExt = (report.outputFilename || '').split('.').pop()?.toUpperCase() || 'TXT';

  return (
    <div style={{
      background: 'var(--navy-2)',
      border: '1px solid var(--border)',
      borderRadius: 12,
      padding: '18px 20px',
      marginBottom: 12,
      transition: 'border-color 0.15s',
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(226,185,111,0.35)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
    >
      {/* ── Input(s) → Output row ── */}
      <div style={{
        display: 'flex', alignItems: 'stretch', gap: 10, flexWrap: 'wrap', marginBottom: 14,
      }}>
        {/* Single or multi input */}
        {isMultiInput ? (
          <div style={{ flex: 2, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <InputFileBox
              label="Input File 1"
              filename={report.inputFilename}
              hasFile={hasInput}
              downloadUrl={getReportInputUrl(report.id, 0)}
            />
            {report.extraInputFiles.map((ef, idx) => (
              <InputFileBox
                key={idx}
                label={`Input File ${idx + 2}`}
                filename={ef.filename}
                hasFile={!!ef.path}
                downloadUrl={getReportInputUrl(report.id, idx + 1)}
              />
            ))}
          </div>
        ) : (
          <InputFileBox
            label="Input File"
            filename={report.inputFilename}
            hasFile={hasInput}
            downloadUrl={getReportInputUrl(report.id, 0)}
          />
        )}

        {/* Arrow */}
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <ArrowRight size={16} style={{ color: 'var(--gold)' }} />
        </div>

        {/* Output box */}
        <div style={{
          flex: 1, minWidth: 200,
          background: isExcel ? 'rgba(63,185,80,0.05)' : 'rgba(226,185,111,0.05)',
          border: `1px solid ${isExcel ? 'rgba(63,185,80,0.25)' : 'rgba(226,185,111,0.2)'}`,
          borderRadius: 9, padding: '10px 14px',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {isExcel
              ? <LayoutGrid size={13} style={{ color: 'var(--green)', flexShrink: 0 }} />
              : <FileText size={13} style={{ color: 'var(--gold)', flexShrink: 0 }} />
            }
            <span style={{ fontSize: 10, color: isExcel ? 'var(--green)' : 'var(--gold)', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
              {isExcel ? 'Excel Dashboard' : 'Analyzed Report'}
            </span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: isExcel ? 'var(--green)' : 'var(--gold)', wordBreak: 'break-all', lineHeight: 1.4 }}>
            {report.outputFilename || (isExcel ? 'Dashboard.xlsx' : 'Analysis.docx')}
          </div>
          <a
            href={getReportOutputUrl(report.id)}
            download={report.outputFilename}
            className={isExcel ? 'btn btn-secondary btn-sm' : 'btn btn-primary btn-sm'}
            style={{ textDecoration: 'none', alignSelf: 'flex-start', marginTop: 2,
              ...(isExcel ? { color: 'var(--green)', borderColor: 'rgba(63,185,80,0.4)' } : {}),
            }}
          >
            <Download size={11} /> Download .{outputExt.toLowerCase()}
          </a>
        </div>
      </div>

        {/* ── Meta row ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        fontSize: 12, color: 'var(--text-muted)', marginBottom: m ? 12 : 0,
      }}>
        {/* Prompt badge */}
        {report.promptName && (
          <span style={{
            fontSize: 11, padding: '2px 9px', borderRadius: 20, fontWeight: 600,
            background: 'rgba(88,166,255,0.12)', color: 'var(--blue)',
            border: '1px solid rgba(88,166,255,0.2)',
          }}>
            {report.promptName}
          </span>
        )}
        {report.company && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Building2 size={12} /><span>{report.company}</span>
          </div>
        )}
        {report.dateRange && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Calendar size={12} /><span>{report.dateRange}</span>
          </div>
        )}
        {report.tokensUsed > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Hash size={12} /><span>{report.tokensUsed.toLocaleString()} tokens</span>
          </div>
        )}

        {/* Time + Delete pushed to the right */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {formatTime(report.processedAt)}
          </span>
          <button
            className="btn btn-danger btn-sm"
            onClick={() => onDelete(report)}
            title="Delete this report"
            style={{ padding: '4px 10px' }}
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>

      {/* ── Metrics pills ── */}
      {m && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {m.totalSpend !== undefined && (
            <span style={{
              fontSize: 11, padding: '3px 10px', borderRadius: 20,
              background: 'rgba(226,185,111,0.12)', color: 'var(--gold)', fontFamily: 'var(--font-mono)',
            }}>
              Total {usd(m.totalSpend)}
            </span>
          )}
          {m.estimatedQuestionableSpend !== undefined && (
            <span style={{
              fontSize: 11, padding: '3px 10px', borderRadius: 20,
              background: 'rgba(192,0,0,0.12)', color: '#f47070', fontFamily: 'var(--font-mono)',
            }}>
              Questionable {usd(m.estimatedQuestionableSpend)}
            </span>
          )}
          {m.transactionCount !== undefined && (
            <span style={{
              fontSize: 11, padding: '3px 10px', borderRadius: 20,
              background: 'rgba(88,166,255,0.12)', color: 'var(--blue)',
            }}>
              {m.transactionCount} transactions
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AnalyzedReportsPage() {
  const [search, setSearch]       = useState('');
  const [toDelete, setToDelete]   = useState(null);
  const qc = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['reports'],
    queryFn:  () => getReports(200),
    refetchInterval: 30000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteReport(id),
    onSuccess: () => {
      toast.success('Report deleted');
      setToDelete(null);
      qc.invalidateQueries({ queryKey: ['reports'] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Delete failed');
      setToDelete(null);
    },
  });

  const allReports = data?.reports || [];
  const filtered   = search.trim()
    ? allReports.filter(r =>
        r.inputFilename?.toLowerCase().includes(search.toLowerCase()) ||
        r.company?.toLowerCase().includes(search.toLowerCase()) ||
        r.outputFilename?.toLowerCase().includes(search.toLowerCase())
      )
    : allReports;

  const groups = groupByDate(filtered);

  return (
    <div className="page">
      {/* Delete confirmation */}
      {toDelete && (
        <DeleteModal
          report={toDelete}
          loading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(toDelete.id)}
          onCancel={() => setToDelete(null)}
        />
      )}

      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 className="page-title">Analyzed Reports</h1>
            <p className="page-subtitle">
              All processed reports — grouped by day. Download input or output, or delete any entry.
            </p>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => refetch()}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 24 }}>
        <Search size={14} style={{
          position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
          color: 'var(--text-muted)', pointerEvents: 'none',
        }} />
        <input
          className="input"
          style={{ paddingLeft: 34 }}
          placeholder="Search by filename or company…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {isLoading && <div className="empty-state"><span className="spinner" /></div>}

      {!isLoading && allReports.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <FileText size={36} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No analyzed reports yet</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            Export a CSV from Dext → Bank → Transactions → Export all, then upload it on the Dext Accounts page.
          </div>
        </div>
      )}

      {!isLoading && allReports.length > 0 && filtered.length === 0 && (
        <div className="empty-state">No reports match your search.</div>
      )}

      {groups.map(([date, reports]) => (
        <div key={date} style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <Calendar size={14} style={{ color: 'var(--gold)' }} />
            <span style={{ fontFamily: 'var(--font-serif)', fontSize: 17, color: 'var(--text)' }}>
              {formatDate(date)}
            </span>
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 20,
              background: 'var(--gold-dim)', color: 'var(--gold)', fontWeight: 600,
            }}>
              {reports.length} report{reports.length !== 1 ? 's' : ''}
            </span>
          </div>

          {reports.map(r => (
            <ReportCard key={r.id} report={r} onDelete={setToDelete} />
          ))}
        </div>
      ))}
    </div>
  );
}
